import { useSearch } from "@redotech/react-util/search";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import Fuse from "fuse.js";
import { createContext, memo, useContext, useMemo, useState } from "react";
import { Tooltip } from "../../../tooltip/tooltip";
import { AvatarStatus, RedoAvatar } from "../../avatar/redo-avatar";
import { RedoListItem } from "../../list/redo-list";
import { RedoListDropdown } from "../../list/redo-list-dropdown";
import * as navigationCss from "../redo-navigation.module.css";

export interface ConnectedTeamData {
  teamId: string;
  name: string;
  logoUrl?: string;
  ticketCount: number;
}

export interface CurrentTeamData {
  teamId: string;
  name: string;
  logoUrl?: string;
}

export interface TeamSwitcherData {
  connectedStoresHaveTickets: boolean;
  connectedStores: ConnectedTeamData[];
  setCurrentTeam: (teamId: string) => void;
  currentTeam: CurrentTeamData;
  addStore: () => void;
}

export const TeamDataContext = createContext<TeamSwitcherData | undefined>(
  undefined,
);

export const RedoNavigationTeamItem = memo(function RedoNavigationTeamItem() {
  const teamData = useContext(TeamDataContext);

  const status: AvatarStatus | undefined = teamData?.connectedStoresHaveTickets
    ? { type: "presence", color: "red" }
    : undefined;

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [switcherDropdownOpen, setSwitcherDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const searchableTeamItems: RedoListItem<string>[] = useMemo(() => {
    if (!teamData) {
      return [];
    }

    const { connectedStores, currentTeam } = teamData;

    const currentTeamItem: RedoListItem<string> = {
      id: currentTeam.teamId,
      value: currentTeam.teamId,
      type: "text",
      text: currentTeam.name,
      leadingItem: {
        type: "avatar",
        imageUrl: currentTeam.logoUrl,
        alt: currentTeam.name,
        name: currentTeam.name,
      },
    };

    const connectedTeams: RedoListItem<string>[] = connectedStores.map(
      (connectedStore) => {
        return {
          id: connectedStore.teamId,
          value: connectedStore.teamId,
          type: "text",
          text: connectedStore.name,
          infoBadge: {
            type: "alternate",
            text: connectedStore.ticketCount,
            color: connectedStore.ticketCount > 0 ? "error" : "black",
          },
          leadingItem: {
            type: "avatar",
            imageUrl: connectedStore.logoUrl,
            alt: connectedStore.name,
            name: connectedStore.name,
          },
        };
      },
    );

    return [currentTeamItem, ...connectedTeams];
  }, [teamData]);

  const fuseSearcher = useMemo(() => {
    return new Fuse(searchableTeamItems, { keys: ["text"], threshold: 0.2 });
  }, [searchableTeamItems]);

  const filteredTeams = useSearch(
    fuseSearcher,
    searchableTeamItems,
    searchText,
  );

  const teamSwitcherList: RedoListItem<string>[] = useMemo(() => {
    const addTeam: RedoListItem<string> = {
      id: "add-team",
      type: "text",
      text: "Add another store",
      leadingItem: { type: "icon", Icon: PlusIcon },
      value: "add-team",
    };

    return [...filteredTeams, addTeam];
  }, [filteredTeams]);

  if (!teamData) {
    return null;
  }

  const { currentTeam, setCurrentTeam } = teamData;

  return (
    <>
      <Tooltip darkMode placement="right" title={currentTeam.name}>
        <button
          className={navigationCss.teamItem}
          onClick={() => setSwitcherDropdownOpen((old) => !old)}
          ref={setDropdownAnchor}
        >
          <RedoAvatar
            content={{
              type: "image",
              imageUrl: currentTeam.logoUrl,
              altText: currentTeam.name,
              backup: { type: "text", text: currentTeam.name },
            }}
            size="sm"
            status={status}
          />
        </button>
      </Tooltip>
      <RedoListDropdown
        darkMode
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={switcherDropdownOpen}
        items={teamSwitcherList}
        itemSelected={({ item }) => {
          if (item.id === "add-team") {
            teamData.addStore();
          } else {
            setCurrentTeam(item.value);
          }
        }}
        searchPlaceholder="Search stores..."
        searchString={searchText}
        selectedItems={currentTeam.teamId}
        setDropdownOpen={(open) => {
          setSwitcherDropdownOpen(open);
          if (!open) {
            setSearchText("");
          }
        }}
        setSearchString={setSearchText}
        size="sm"
      />
    </>
  );
});
